/**
 * @file AddSalaryType.jsx
 * @description Component for creating a new Salary Type in the HRMS Payroll module.
 * It manages form state, validation, and submission using custom hooks.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Create } from "adminApi/SalaryTypeApi/SalaryType";
import Spinner from "Common/Globle/Spinner";
import AddSalaryTypeForm from "./components/AddSalaryTypeForm";
import fields from "../json/SalaryType.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import { SalaryTypeRules } from "validation/ValidationRules";
import { validateForm } from "utils/validation";

const AddSalaryType = () => {
    const navigate = useNavigate();

    // Extract form configuration from JSON
    const { formdata } = fields;

    // Initialize form values based on field definitions
    // Defaulting status to '1' (Active) and default_amount to '0'
    const initialValues = formdata.reduce((acc, field) => {
        acc[field.name] = field.name === "status" ? "1" : field.name === "default_amount" ? "0" : "";
        return acc;
    }, {});

    /**
     * useFormChange hook manages form field updates and local validation errors.
     */
    const {
        values,
        errors,
        handleChange,
        setErrors
    } = useFormChange(initialValues, SalaryTypeRules);

    /**
     * useFormSubmit hook handles the API call and form submission lifecycle.
     */
    const { loading, handleSubmit } = useFormSubmit(
        async (formValues) => {
            // Prepare FormData for the backend API
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => formData.append(key, val ?? ""));
            return await Create({ data: formData });
        },
        (vals) => {
            // Client-side validation before submission
            const err = validateForm(vals, SalaryTypeRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1), // On success: navigate back to the list
        "Salary Type created successfully"
    );

    return (
        <>
            {/* Show loading spinner during submission */}
            {loading && <Spinner isSpinner />}

            {/* Render the presentation component for the form */}
            <AddSalaryTypeForm
                fields={fields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loading}
                title="Create Salary Type"
            />
        </>
    );
};

export default AddSalaryType;

